import { useState } from "react";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { edgeMeta } from "@/lib/vocabulary";
import { ALL_EDGE_TYPES } from "@/lib/vocabulary";
import type { StrainGraph, RelationType } from "@/types";
import { useStore } from "@/store/useStore";
import { useToast } from "@/components/Toast";

export function AddEdgeModal({
  open, onClose, graph,
}: { open: boolean; onClose: () => void; graph: StrainGraph }) {
  const addEdge = useStore((s) => s.addEdge);
  const toast = useToast((s) => s.push);
  const [source, setSource] = useState(graph.nodes[0]?.id ?? "");
  const [target, setTarget] = useState(graph.nodes[1]?.id ?? "");
  const [relation, setRelation] = useState<RelationType>("supports");
  const [label, setLabel] = useState("");
  const [isUndercut, setIsUndercut] = useState(false);
  const [targetEdge, setTargetEdge] = useState(graph.edges[0]?.id ?? "");

  const commit = () => {
    if (isUndercut && targetEdge) {
      addEdge(graph.strain.slug, {
        source, target_edge: targetEdge, relation: "undercuts",
        label: label || "undercut", provenance: "user",
      });
      toast({ tone: "ok", title: "Undercut added", desc: "Targets an inference, not a node." });
    } else {
      addEdge(graph.strain.slug, {
        source, target, relation, label: label || edgeMeta(relation).verb, provenance: "user",
      });
      toast({ tone: "ok", title: "Edge added", desc: `${edgeMeta(relation).label} connection created.` });
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a connection"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={commit}>Add connection</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Relation type</label>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            {ALL_EDGE_TYPES.map((rt) => {
              const m = edgeMeta(rt);
              const Icon = m.icon;
              const active = (!isUndercut && relation === rt) || (isUndercut && rt === "undercuts");
              return (
                <button
                  key={rt}
                  onClick={() => {
                    if (rt === "undercuts") setIsUndercut(true);
                    else { setIsUndercut(false); setRelation(rt); }
                  }}
                  className={`inline-flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs transition-colors ${
                    active
                      ? "border-[var(--color-primary)] bg-[var(--color-surface-2)] text-[var(--color-ink)]"
                      : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]"
                  }`}
                  style={{ color: active ? m.color : undefined }}
                >
                  <Icon size={13} /> {m.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[var(--color-ink-faint)] mt-1.5">
            {edgeMeta(isUndercut ? "undercuts" : relation).description}
          </p>
        </div>

        {isUndercut ? (
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Inference to undercut</label>
            <select
              value={targetEdge}
              onChange={(e) => setTargetEdge(e.target.value)}
              className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)]"
            >
              {graph.edges.map((e) => {
                const sn = graph.nodes.find((n) => n.id === e.source);
                return <option key={e.id} value={e.id}>{edgeMeta(e.relation).label}: {sn?.text.slice(0, 40)}…</option>;
              })}
            </select>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Source node</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)]">
              {graph.nodes.map((n) => <option key={n.id} value={n.id}>{n.text.slice(0, 50)}</option>)}
            </select>
          </div>
          {!isUndercut && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Target node</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)]">
                {graph.nodes.map((n) => <option key={n.id} value={n.id}>{n.text.slice(0, 50)}</option>)}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Label (optional)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={edgeMeta(isUndercut ? "undercuts" : relation).verb}
            className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)]"
          />
        </div>
      </div>
    </Modal>
  );
}
